import { createAsyncThunk } from '@reduxjs/toolkit'
import { initSdk, sdk } from '../../../utils/signer/utils'
import { Hex, L1ChainId } from '../../../types'
import type { Web3Provider } from '@ethersproject/providers'
import { RootState } from '../../index'
import i18n from '../../../i18n'
import { numberToHex } from '../../../utils/number'
import restore from '../../../utils/restore'
import { updateModal } from '../../app/actions/modalActions'
import { AccountInterface } from 'starknet'
import { handleWasmSdkError } from '../../../utils/signer/error'

export function isStarknetProvider(provider: Web3Provider | AccountInterface) {
  return !!(provider as AccountInterface)?.deploy
}

function recoverKeys(address: string) {
  return restore.get(address)?.signature
}
const connectByEvmProvider = async (
  provider: Web3Provider,
  needRecoverKeys: boolean = false,
) => {
  if (!provider) {
    throw new Error('Web3Provider not found')
  }
  if (!provider.getSigner) {
    throw new Error('getSigner is undefined in the provider')
  }

  const network = await provider.detectNetwork()
  if (!network?.chainId) {
    throw new Error('Cannot detect network')
  }

  const web3Signer = provider.getSigner()
  const address = await web3Signer.getAddress()

  if (!address) {
    throw new Error(i18n.t('common-not-address'))
  }

  const chainId = numberToHex(network.chainId)

  const newSigner = sdk.newEthereumRpcSigner(web3Signer)

  let seed: Hex | undefined
  if (needRecoverKeys) {
    // if user has saved seed, try to restore
    seed = recoverKeys(address)
  }

  // If seed is undefined, try to sign message to generate a new seed
  // if (!seed) {
  //   seed = await web3Signer
  //     .signMessage(GENERATE_ZKLINK_KEY_MESSAGE)
  //     .catch((e) => {
  //       if (UserRejectedError.is(e)) {
  //         throw new UserRejectedError(e.message)
  //       } else {
  //         throw e
  //       }
  //     })
  // }

  return {
    address,
    chainId,
    newSigner,
    seed,
  }
}

const connectByStarknetAccount = async (
  account: AccountInterface,
  needRecoverKeys: boolean = false,
) => {
  if (!account) {
    throw new Error('Starknet account not found')
  }
  if (!account.signer) {
    throw new Error('Starknet signer not found')
  }

  const chainId = await account.getChainId()
  if (!chainId) {
    throw new Error('Starknet chainId notFound')
  }

  const { address } = account
  if (!address) {
    throw new Error(i18n.t('common-not-address'))
  }
  const pubKey = await account.signer.getPubKey()

  const newSigner = sdk.newStarknetRpcSigner(account, pubKey, chainId)

  let seed: Hex | undefined
  if (needRecoverKeys) {
    // if user has saved seed, try to restore
    seed = recoverKeys(address)
  }

  return {
    address,
    chainId,
    newSigner,
    seed,
  }
}

export const connectLinkWalletAction = createAsyncThunk<
  {
    signer: sdk.JsonRpcSigner
    chainId: L1ChainId
  },
  {
    provider: Web3Provider | AccountInterface
  },
  {
    state: RootState
  }
>('link/connectLinkWallet', async ({ provider }, { dispatch, getState }) => {
  await initSdk()
  const { settings } = getState()
  const { save } = settings

  const { address, chainId, newSigner, seed } = isStarknetProvider(provider)
    ? await connectByStarknetAccount(provider as AccountInterface, save)
    : await connectByEvmProvider(provider as Web3Provider, save)

  // Seed may be empty string, however, starknet is not support empty string seed, must be undefined or hex string
  await newSigner.initZklinkSigner(seed).catch(handleWasmSdkError)

  if (save) {
    restore.set(address, newSigner.signatureSeed())
  }

  dispatch(updateModal({ modal: 'wallets', open: false }))

  return {
    chainId,
    signer: newSigner,
  }
})
